import { useCallback, useEffect, useMemo, useRef } from "react";
import type { UseFormReturn } from "react-hook-form";

import { CollectionFilterIdEnum, type OptionItem } from "@/api/admin";
import {
  type BaseEntityLocation,
  useGetLocationDataIdQuery,
} from "@/api/admin/locations";

import type { FilterItem, NewCategoryFormType } from "./types";

const LOCATION_FILTER_IDS = new Set<string>([
  CollectionFilterIdEnum.AREAS,
  CollectionFilterIdEnum.STREETS,
  CollectionFilterIdEnum.LANDMARKS,
]);

export function isCollectionLocationFilter(
  filterId: string | undefined
): boolean {
  return !!filterId && LOCATION_FILTER_IDS.has(filterId);
}

function toOption(item: BaseEntityLocation): OptionItem {
  return {
    id: item.id,
    name: item.nameEn,
  };
}

interface UseCollectionLocationFiltersParams {
  form: UseFormReturn<NewCategoryFormType>;
  filters: FilterItem[];
}

export function useCollectionLocationFilters({
  form,
  filters,
}: UseCollectionLocationFiltersParams) {
  const selectedCityIds =
    filters.find(filter => filter.id === CollectionFilterIdEnum.CITIES)
      ?.value ?? [];
  const selectedCityId = selectedCityIds.length === 1 ? selectedCityIds[0] : "";
  const {
    data: locationData,
    isPending,
    isError,
  } = useGetLocationDataIdQuery(selectedCityId, selectedCityIds.length === 1);
  const citySelectionKey = JSON.stringify(selectedCityIds);
  const previousCitySelectionRef = useRef<string | null>(null);

  useEffect(() => {
    if (previousCitySelectionRef.current === null) {
      previousCitySelectionRef.current = citySelectionKey;
      return;
    }

    if (previousCitySelectionRef.current === citySelectionKey) return;

    const currentFilters = form.getValues("filters") ?? [];
    currentFilters.forEach((filter, index) => {
      if (isCollectionLocationFilter(filter.id)) {
        form.setValue(`filters.${index}.value`, [], {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    });
    previousCitySelectionRef.current = citySelectionKey;
  }, [citySelectionKey, form]);

  const locationOptions = useMemo<Record<string, OptionItem[]>>(
    () => ({
      [CollectionFilterIdEnum.AREAS]: locationData?.areas.map(toOption) ?? [],
      [CollectionFilterIdEnum.STREETS]:
        locationData?.streets.map(toOption) ?? [],
      [CollectionFilterIdEnum.LANDMARKS]:
        locationData?.landmarks.map(toOption) ?? [],
    }),
    [locationData]
  );

  const getLocationOptions = useCallback(
    (filterId: string | undefined): OptionItem[] | null => {
      if (!filterId || !isCollectionLocationFilter(filterId)) return null;
      return locationOptions[filterId] ?? [];
    },
    [locationOptions]
  );

  const isLocationFilterDisabled = useCallback(
    (filterId: string | undefined): boolean =>
      isCollectionLocationFilter(filterId) &&
      (selectedCityIds.length !== 1 || isPending || isError),
    [isError, isPending, selectedCityIds.length]
  );

  const locationPlaceholder =
    selectedCityIds.length === 0
      ? "Select city first"
      : selectedCityIds.length > 1
        ? "Select only one city to show relevant data"
        : isPending
          ? "Loading..."
          : isError
            ? "Failed to load options"
            : "Select multiple";

  return {
    getLocationOptions,
    isLocationFilterDisabled,
    locationPlaceholder,
  };
}
